import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/home/Hero";
import { fetchBlogPosts, BlogFrontMatter } from "@/lib/blog";
import { fetchCourses } from "@/lib/courses";
import type { Course } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/lib/utils";

export default function Index({ lang = "en" }: { lang?: string }) {
  const { t, i18n } = useTranslation();
  const [latestBlogs, setLatestBlogs] = useState<BlogFrontMatter[]>([]);
  const [popularCourses, setPopularCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState({
    blogs: true,
    courses: true
  });
  
  // Sync the i18n language with the component's lang prop
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Fetch latest blog posts
  useEffect(() => {
    const fetchLatestPosts = async () => {
      setLoading(prev => ({ ...prev, blogs: true }));
      try {
        const posts = await fetchBlogPosts(lang);
        // Sort by date and get the 3 most recent
        const sortedPosts = [...posts].sort((a, b) => {
          // If using numeric date field
          if (typeof a.date === 'number' && typeof b.date === 'number') {
            return b.date - a.date;
          }
          // Fallback to string comparison if dates are strings
          return String(b.date).localeCompare(String(a.date));
        }).slice(0, 3);
        setLatestBlogs(sortedPosts);
      } catch (error) {
        console.error("Error fetching latest blog posts:", error);
      } finally {
        setLoading(prev => ({ ...prev, blogs: false }));
      }
    };

    fetchLatestPosts();
  }, [lang]);

  // Fetch courses
  useEffect(() => {
    const fetchPopularCourses = async () => {
      setLoading(prev => ({ ...prev, courses: true }));
      try {
        const coursesData = await fetchCourses(lang);
        // Get up to 3 courses
        setPopularCourses(coursesData.slice(0, 3));
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(prev => ({ ...prev, courses: false }));
      }
    };

    fetchPopularCourses();
  }, [lang]);

  // Helper function for language-aware URLs
  const getLocalizedPath = (basePath: string) => {
    return lang === 'en' ? basePath : `/${lang}${basePath}`;
  };

  return (
    <div className="min-h-screen">
      <Hero lang={lang} />
    
      {/* Features Section - Commented out */}
      {/* 
      <section className="py-20 container">
        <h2 className="text-3xl font-bold mb-8 text-center">{t('home.features.title')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="card-enhanced p-6">
            <h3 className="text-xl font-bold mb-3">{t('home.features.feature1.title')}</h3>
            <p className="text-muted-foreground">{t('home.features.feature1.description')}</p>
          </div>
          
          <div className="card-enhanced p-6">
            <h3 className="text-xl font-bold mb-3">{t('home.features.feature2.title')}</h3>
            <p className="text-muted-foreground">{t('home.features.feature2.description')}</p>
          </div>
          
          <div className="card-enhanced p-6">
            <h3 className="text-xl font-bold mb-3">{t('home.features.feature3.title')}</h3>
            <p className="text-muted-foreground">{t('home.features.feature3.description')}</p>
          </div>
        </div>
      </section>
      */}
      
      {/* Latest Blog Posts Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold">{t('home.latest_blogs.title')}</h2>
            <Link 
              to={getLocalizedPath("/blog")} 
              className="flex items-center text-primary hover:underline"
            >
              {t('home.latest_blogs.view_all')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {loading.blogs ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : latestBlogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">{t('no_results')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestBlogs.map((post) => (
                <Link 
                  key={post.slug}
                  to={getLocalizedPath(`/blog/${post.slug}`)}
                  className="group"
                >
                  <div className="card-enhanced bg-card overflow-hidden h-full flex flex-col">
                    {post.cover && (
                      <div className="w-full h-48 overflow-hidden">
                        <img 
                          src={typeof post.cover === 'string' ? post.cover : post.cover?.image || ''} 
                          alt={post.cover_alt || post.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-in-out" 
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">{post.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-grow line-clamp-3">
                        {post.excerpt && post.excerpt.trim().toLowerCase() !== 'no_excerpt' ? post.excerpt : ''}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Popular Courses Section */}
      <section className="py-20">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold">{t('home.popular_courses.title')}</h2>
            <Link 
              to={getLocalizedPath("/courses")} 
              className="flex items-center text-primary hover:underline"
            >
              {t('home.popular_courses.view_all')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {loading.courses ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : popularCourses.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">{t('no_courses_found')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {popularCourses.map((course) => (
                <Link 
                  key={course.slug}
                  to={getLocalizedPath(`/courses/${course.slug}`)}
                  className="group"
                >
                  <div className="card-enhanced bg-card overflow-hidden h-full flex flex-col">
                    {course.cover && (
                      <div className="w-full overflow-hidden">
                        <img
                          src={course.cover}
                          alt={course.title}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-grow line-clamp-3">
                        {course.description}
                      </p>

                      <div className="mt-auto">
                        {course.is_free ? (
                          <Badge variant="outline" className="bg-primary/10 text-primary">
                            {t('free')}
                          </Badge>
                        ) : course.price_cents && course.default_price?.currency ? (
                          <Badge variant="outline" className="bg-primary/10 text-primary">
                            {formatPrice(course.price_cents, course.default_price.currency)}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Testimonials Section - Commented out */}
      {/* 
      <section className="py-20 bg-secondary/30">
        <div className="container">
          <h2 className="text-3xl font-bold mb-8 text-center">{t('home.testimonials.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card-enhanced p-6">
              <p className="italic mb-4">"{t('home.testimonials.testimonial1.quote')}"</p>
              <div className="font-bold">{t('home.testimonials.testimonial1.author')}</div>
            </div>
            
            <div className="card-enhanced p-6">
              <p className="italic mb-4">"{t('home.testimonials.testimonial2.quote')}"</p>
              <div className="font-bold">{t('home.testimonials.testimonial2.author')}</div>
            </div>
          </div>
        </div>
      </section>
      */}
    </div>
  );
}
